import { observer } from 'mobx-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';
import classes from './drugReferenceByNDCTable.module.scss';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import searchIcon from 'assets/icons/PPSIcons/searchIcon.svg';
import { useDrugReferenceFgNdcTable } from './useDrugReferenceByNDC';
import AddRowModalDrugReferenceFgNdc from './utils/AddRowModalDrugReferenceByNDC';
import DeleteConfirmationModalDrugReferenceFgNdc from './utils/DeleteConfirmationModalDrugReferenceByNDC';

interface DrugReferenceFgNdcTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const DrugReferenceFgNdcTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: DrugReferenceFgNdcTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;
    const {
      drugReferenceData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      handleSearchChange,
      prepareFormData,
      onPageChange,
      handleExportToExcel
    } = useDrugReferenceFgNdcTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    if (isLoading && !drugReferenceData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.drugReferenceFgNdcTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Drug Reference By NDC Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                className={classes.searchInput}
                placeholder="Search..."
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>
            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />
            {/* {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )} */}
          </div>
        </div>

        <DataTable
          value={drugReferenceData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? drugReferenceData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="fg_ndc" header="NDC" />
          <Column field="drug_name" header="Drug Name" />
          <Column field="bottle_stock_code" header="Bottle Stock Code" />
          <Column field="bottle_description" header="Bottle Description" />
          <Column field="cap_stock_code" header="Cap Stock Code" />
          <Column field="cap_description" header="Cap Description" />
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalDrugReferenceFgNdc
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
        />

        <DeleteConfirmationModalDrugReferenceFgNdc
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default DrugReferenceFgNdcTable;
